import { ref, onBeforeUnmount } from 'vue';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/composables/useToast';

/**
 * Simplified Realtime Connection Manager
 * 
 * This composable provides a simple, reliable realtime connection approach
 * focusing on stability over optimization.
 * 
 * KEY IMPROVEMENTS:
 * - Direct Supabase connections (no complex shared manager)
 * - Simple, predictable error handling
 * - Reduced reconnection aggression
 * - Better tab visibility handling
 */
export function useRealtimeConnection() {
  const toast = useToast();
  
  // Connection state
  const isConnected = ref(false);
  const connectionState = ref('disconnected');
  const isTabVisible = ref(true);
  const subscriptions = ref(new Map());
  
  // Heartbeat tracking
  let heartbeatInterval = null;
  let connectionCheckInterval = null;
  let reconnectTimeout = null;
  let lastHeartbeatTime = Date.now();
  
  // Generate unique component ID for logging
  const componentId = `component_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  console.log(`[RealtimeConnection] Component ${componentId} initialized`);
  
  // Visibility change handler
  const handleVisibilityChange = () => {
    isTabVisible.value = !document.hidden;
    
    if (isTabVisible.value) {
      console.log('[RealtimeConnection] Tab became visible, checking connection');
      // Tab became visible - only check connection after a reasonable delay
      setTimeout(() => {
        if (connectionState.value === 'CLOSED' || connectionState.value === 'CHANNEL_ERROR') {
          console.log('[RealtimeConnection] Connection was closed while tab was hidden, reconnecting');
          checkAndReconnect();
        }
      }, 3000); // Wait 3 seconds for potential network activity
    } else {
      console.log('[RealtimeConnection] Tab became hidden');
      // Tab hidden - connections will stay active
    }
  };
  
  // Check connection health and reconnect if needed
  const checkAndReconnect = async () => {
    const timeSinceLastHeartbeat = Date.now() - lastHeartbeatTime;
    const maxHeartbeatGap = 180000; // 3 minutes (less aggressive)
    
    // Only reconnect if truly disconnected
    if (connectionState.value === 'CLOSED' || 
        connectionState.value === 'CHANNEL_ERROR' || 
        connectionState.value === 'TIMED_OUT') {
      console.log('[RealtimeConnection] Connection is in error state, reconnecting...', {
        timeSinceLastHeartbeat,
        connectionState: connectionState.value
      });
      
      await reconnectAll();
    } else if (timeSinceLastHeartbeat > maxHeartbeatGap && 
               connectionState.value !== 'SUBSCRIBED') {
      console.log('[RealtimeConnection] Connection appears stale, reconnecting...', {
        timeSinceLastHeartbeat,
        connectionState: connectionState.value
      });
      
      await reconnectAll();
    }
  };
  
  // Reconnect all subscriptions
  const reconnectAll = async () => {
    console.log('[RealtimeConnection] Reconnecting all subscriptions');
    
    // Test network connectivity first
    try {
      const { error } = await supabase.from('orders').select('id').limit(1);
      if (error) {
        console.log('[RealtimeConnection] Network test failed, skipping reconnection:', error);
        return;
      }
    } catch (error) {
      console.log('[RealtimeConnection] Network unavailable, skipping reconnection:', error);
      return;
    }
    
    // Store current subscriptions data
    const subscriptionsData = Array.from(subscriptions.value.entries());
    
    // Clean up existing subscriptions
    cleanup();
    
    // Wait before reconnecting
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Recreate subscriptions
    let successCount = 0;
    for (const [key, data] of subscriptionsData) {
      try {
        await createSubscription(key, data.config, data.callback);
        successCount++;
      } catch (error) {
        console.error(`[RealtimeConnection] Failed to recreate subscription ${key}:`, error);
      }
    }
    
    // Show user notification only for successful reconnections
    if (isTabVisible.value && successCount > 0) {
      toast.success('Real-time connection restored');
    }
  };
  
  // Create a subscription with simple error handling
  const createSubscription = async (key, config, callback) => {
    try {
      console.log(`[RealtimeConnection] Creating subscription: ${key}`);
      
      const subscription = supabase
        .channel(key)
        .on(
          'postgres_changes',
          config,
          (payload) => {
            lastHeartbeatTime = Date.now();
            console.log(`[RealtimeConnection] Received data for ${key}:`, payload);
            callback(payload);
          }
        )
        .subscribe((status) => {
          console.log(`[RealtimeConnection] Subscription ${key} status:`, status);
          connectionState.value = status;
          isConnected.value = status === 'SUBSCRIBED';
          
          if (status === 'SUBSCRIBED') {
            lastHeartbeatTime = Date.now();
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            console.error(`[RealtimeConnection] Subscription ${key} failed:`, status);
            // Schedule reconnection with simple delay
            scheduleReconnection();
          }
        });
      
      subscriptions.value.set(key, {
        subscription,
        config,
        callback
      });
      
      return subscription;
    } catch (error) {
      console.error(`[RealtimeConnection] Error creating subscription ${key}:`, error);
      throw error;
    }
  };
  
  // Schedule automatic reconnection
  const scheduleReconnection = () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
    }
    
    reconnectTimeout = setTimeout(async () => {
      if (isTabVisible.value && 
          (connectionState.value === 'CLOSED' || 
           connectionState.value === 'CHANNEL_ERROR' || 
           connectionState.value === 'TIMED_OUT')) {
        console.log('[RealtimeConnection] Attempting scheduled reconnection');
        await reconnectAll();
      }
    }, 15000); // Reconnect after 15 seconds (less aggressive)
  };
  
  // Start connection monitoring
  const startMonitoring = () => {
    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Periodic connection health check
    connectionCheckInterval = setInterval(() => {
      if (isTabVisible.value) {
        checkAndReconnect();
      }
    }, 120000); // Check every 2 minutes when tab is visible
    
    // Simple heartbeat check - reduced frequency to minimize database load
    heartbeatInterval = setInterval(async () => {
      if (isConnected.value && isTabVisible.value) {
        // Test actual connection health by making a simple query
        try {
          const { error } = await supabase.from('orders').select('id').limit(1);
          if (!error) {
            lastHeartbeatTime = Date.now();
          } else {
            console.warn('[RealtimeConnection] Heartbeat query failed:', error);
          }
        } catch (error) {
          console.warn('[RealtimeConnection] Heartbeat test failed:', error);
        }
      }
    }, 300000); // Check every 5 minutes (reduced from 90 seconds)
  };
  
  // Stop monitoring
  const stopMonitoring = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    
    if (connectionCheckInterval) {
      clearInterval(connectionCheckInterval);
      connectionCheckInterval = null;
    }
    
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
    
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
  };
  
  // Clean up all subscriptions
  const cleanup = () => {
    console.log('[RealtimeConnection] Cleaning up subscriptions');
    
    for (const [key, data] of subscriptions.value.entries()) {
      if (data.subscription) {
        supabase.removeChannel(data.subscription);
      }
    }
    
    subscriptions.value.clear();
    isConnected.value = false;
    connectionState.value = 'disconnected';
  };
  
  // Initialize monitoring on first use
  let isInitialized = false;
  const initialize = () => {
    if (!isInitialized) {
      startMonitoring();
      isInitialized = true;
      console.log('[RealtimeConnection] Connection monitoring initialized');
    }
  };
  
  // Auto cleanup on component unmount
  onBeforeUnmount(() => {
    stopMonitoring();
    cleanup();
  });
  
  return {
    isConnected,
    connectionState,
    isTabVisible,
    subscriptions,
    createSubscription,
    reconnectAll,
    cleanup,
    initialize
  };
} 